import { useState } from 'react';
import { useApiResource } from '@/hooks/useApiResource';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { CheckSquare, Trash2, Target, FolderKanban, Check } from 'lucide-react';
import { AppLayout } from '@/components/AppLayout';
import { AppHeader } from '@/components/AppHeader';
import { Chip, EmptyState } from '@/components/Primitives';
import { FullScreenLoader } from '@/components/FullScreenLoader';
import { FAB, FormDrawer, TextField, TextAreaField, SelectField, Field } from '@/components/FormControls';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import api from '@/lib/api';
import {
  TASK_PRIORITIES, TASK_STATUSES, priorityDot, priorityChip, formatDate, isToday, isPast,
} from '@/lib/format';
import { useLang } from '@/context/LanguageContext';
import { cn } from '@/lib/utils';

const emptyTask = {
  title: '', description: '', priority: 'Medium', dueDate: '',
  status: 'To Do', relatedGoalId: 'none', relatedProjectId: 'none',
};
const cardEnter = { initial: { opacity: 0, y: 6 }, animate: { opacity: 1, y: 0 } };

export default function Tasks() {
  const { t, tEnum, lang } = useLang();
  const { data, loading, reload: load } = useApiResource(
    async () => {
      const [tk, g, p] = await Promise.all([api.get('/tasks'), api.get('/goals'), api.get('/projects')]);
      return { tasks: tk.data, goals: g.data, projects: p.data };
    },
    { errorMessage: t('tasks.loadError') }
  );
  const tasks = data?.tasks || [];
  const goals = data?.goals || [];
  const projects = data?.projects || [];
  const [filter, setFilter] = useState('Today');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyTask);
  const [saving, setSaving] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  const goalName = (id) => goals.find((g) => g.id === id)?.title;
  const projectName = (id) => projects.find((p) => p.id === id)?.name;

  const openCreate = () => {
    setEditing(null);
    setForm(emptyTask);
    setDrawerOpen(true);
  };

  const openEdit = (task) => {
    setEditing(task);
    setForm({
      ...emptyTask, ...task,
      dueDate: task.dueDate || '',
      relatedGoalId: task.relatedGoalId || 'none',
      relatedProjectId: task.relatedProjectId || 'none',
    });
    setDrawerOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error(t('tasks.titleRequired')); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        dueDate: form.dueDate || null,
        relatedGoalId: form.relatedGoalId === 'none' ? null : form.relatedGoalId,
        relatedProjectId: form.relatedProjectId === 'none' ? null : form.relatedProjectId,
      };
      if (editing) {
        await api.put(`/tasks/${editing.id}`, payload);
        toast.success(t('tasks.updated'));
      } else {
        await api.post('/tasks', payload);
        toast.success(t('tasks.added'));
      }
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('tasks.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const toggleDone = async (task) => {
    try {
      await api.put(`/tasks/${task.id}`, { status: task.status === 'Done' ? 'To Do' : 'Done' });
      if (task.status !== 'Done') toast.success(t('tasks.done'));
      load();
    } catch (e) {
      toast.error(t('tasks.updateError'));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/tasks/${deleteId}`);
      toast.success(t('tasks.deleted'));
      setDeleteId(null);
      setDrawerOpen(false);
      load();
    } catch (e) {
      toast.error(t('tasks.deleteError'));
    }
  };

  const filtered = tasks.filter((task) => {
    if (filter === 'Completed') return task.status === 'Done';
    if (task.status === 'Done') return false;
    if (filter === 'Today') return isToday(task.dueDate);
    if (filter === 'Upcoming') return !isToday(task.dueDate);
    return true;
  });

  if (loading) return <FullScreenLoader label={t('tasks.loader')} />;

  const filters = ['Today', 'Upcoming', 'Completed'];

  return (
    <AppLayout>
      <AppHeader title={t('tasks.title')} subtitle={t('tasks.subtitle')} />

      <div className="no-scrollbar mt-3 flex gap-2 overflow-x-auto pb-1">
        {filters.map((f) => (
          <button
            key={f}
            data-testid={`tasks-filter-${f.toLowerCase()}-chip`}
            onClick={() => setFilter(f)}
            className={cn(
              'shrink-0 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors',
              filter === f
                ? 'border-brand-cyan/40 bg-brand-cyan/15 text-cyan-100'
                : 'border-white/10 bg-white/[0.05] text-foreground/60'
            )}
          >
            {tEnum(f)}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {filtered.length === 0 ? (
          <EmptyState
            icon={CheckSquare}
            title={filter === 'Completed' ? t('tasks.emptyCompletedTitle') : t('tasks.emptyTitle')}
            subtitle={filter === 'Today' ? t('tasks.emptyTodaySubtitle') : t('tasks.emptySubtitle')}
          />
        ) : (
          filtered.map((task, i) => (
            <motion.div
              key={task.id}
              initial={cardEnter.initial}
              animate={cardEnter.animate}
              transition={{ duration: 0.25, delay: i * 0.02 }}
              data-testid={`task-row-${task.id}`}
              className="flex items-center gap-3 rounded-xl border border-white/10 bg-white/[0.05] px-3 py-3"
            >
              <button
                data-testid={`task-toggle-${task.id}`}
                onClick={() => toggleDone(task)}
                className={cn(
                  'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-colors',
                  task.status === 'Done'
                    ? 'border-emerald-300 bg-emerald-400/20 text-emerald-300'
                    : 'border-white/20 text-transparent hover:border-emerald-300'
                )}
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button onClick={() => openEdit(task)} className="min-w-0 flex-1 text-left">
                <div className="flex items-center gap-2">
                  <span className={cn('h-2 w-2 shrink-0 rounded-full', priorityDot(task.priority))} />
                  <p className={cn('truncate text-sm font-medium', task.status === 'Done' ? 'text-foreground/40 line-through' : 'text-foreground/90')}>
                    {task.title}
                  </p>
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  {task.dueDate ? (
                    <span className={cn('text-[11px]', isPast(task.dueDate) && task.status !== 'Done' ? 'text-rose-300' : 'text-foreground/50')}>
                      {formatDate(task.dueDate, lang)}
                    </span>
                  ) : null}
                  {goalName(task.relatedGoalId) ? (
                    <Chip className="border-white/10 bg-white/[0.05] text-foreground/60">
                      <Target className="h-2.5 w-2.5" /> {goalName(task.relatedGoalId)}
                    </Chip>
                  ) : null}
                  {projectName(task.relatedProjectId) ? (
                    <Chip className="border-white/10 bg-white/[0.05] text-foreground/60">
                      <FolderKanban className="h-2.5 w-2.5" /> {projectName(task.relatedProjectId)}
                    </Chip>
                  ) : null}
                </div>
              </button>
              <Chip className={priorityChip(task.priority)}>{tEnum(task.priority)}</Chip>
            </motion.div>
          ))
        )}
      </div>

      <FAB onClick={openCreate} testId="tasks-add-fab" label={t('tasks.addFab')} />

      <FormDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        title={editing ? t('tasks.editTitle') : t('tasks.newTitle')}
        onSubmit={save}
        loading={saving}
        submitLabel={editing ? t('common.saveChanges') : t('tasks.addTask')}
        submitTestId="task-create-save-button"
      >
        <TextField
          label={t('tasks.fieldTitle')}
          testId="task-title-input"
          value={form.title}
          onChange={(e) => setForm({ ...form, title: e.target.value })}
          placeholder={t('tasks.titlePlaceholder')}
        />
        <TextAreaField
          label={t('tasks.description')}
          testId="task-description-input"
          value={form.description}
          onChange={(e) => setForm({ ...form, description: e.target.value })}
          placeholder={t('tasks.descriptionPlaceholder')}
        />
        <div className="grid grid-cols-2 gap-3">
          <SelectField
            label={t('tasks.priority')}
            testId="task-priority-select"
            value={form.priority}
            onValueChange={(v) => setForm({ ...form, priority: v })}
            options={TASK_PRIORITIES}
          />
          <SelectField
            label={t('tasks.status')}
            testId="task-status-select"
            value={form.status}
            onValueChange={(v) => setForm({ ...form, status: v })}
            options={TASK_STATUSES}
          />
        </div>
        <TextField
          label={t('tasks.dueDate')}
          testId="task-duedate-input"
          type="date"
          value={form.dueDate || ''}
          onChange={(e) => setForm({ ...form, dueDate: e.target.value })}
        />
        <Field label={t('tasks.relatedGoal')}>
          <Select value={form.relatedGoalId} onValueChange={(v) => setForm({ ...form, relatedGoalId: v })}>
            <SelectTrigger data-testid="task-goal-select" className="h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground">
              <SelectValue placeholder={t('common.none')} />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0A1730] text-foreground">
              <SelectItem value="none" className="focus:bg-white/[0.08]">{t('common.none')}</SelectItem>
              {goals.map((g) => (
                <SelectItem key={g.id} value={g.id} className="focus:bg-white/[0.08]">{g.title}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <Field label={t('tasks.relatedProject')}>
          <Select value={form.relatedProjectId} onValueChange={(v) => setForm({ ...form, relatedProjectId: v })}>
            <SelectTrigger data-testid="task-project-select" className="h-11 rounded-xl border-white/10 bg-white/[0.06] text-foreground">
              <SelectValue placeholder={t('common.none')} />
            </SelectTrigger>
            <SelectContent className="border-white/10 bg-[#0A1730] text-foreground">
              <SelectItem value="none" className="focus:bg-white/[0.08]">{t('common.none')}</SelectItem>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id} className="focus:bg-white/[0.08]">{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        {editing ? (
          <button
            data-testid="task-delete-button"
            onClick={() => setDeleteId(editing.id)}
            className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-rose-300/20 bg-rose-400/10 px-4 py-2.5 text-sm font-medium text-rose-200 hover:bg-rose-400/15"
          >
            <Trash2 className="h-4 w-4" /> {t('tasks.deleteTask')}
          </button>
        ) : null}
      </FormDrawer>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="mx-auto max-w-[420px] border-white/10 bg-[#0A1730] text-foreground">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('tasks.confirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription className="text-foreground/60">{t('common.cannotUndo')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="border-white/10 bg-white/[0.06] text-foreground hover:bg-white/[0.1]">{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction data-testid="task-delete-confirm" onClick={doDelete} className="bg-rose-500 text-white hover:bg-rose-500/90">{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AppLayout>
  );
}
